<template>
  <b-card class="bulk-patient-import-form">
    <h5 class="mb-4">
      <i class="fas fa-file-import mr-2 text-primary"></i>
      Bulk Patient Import
    </h5>
    <b-form @submit.prevent="handleSubmit">
      <ClinicSearchSelect
        v-model="selectedClinic"
        label="Select Clinic *"
        :state="$v.selectedClinic.$dirty ? !$v.selectedClinic.$error : null"
      />
      <b-form-invalid-feedback :state="!$v.selectedClinic.$error">
        Please select a clinic
      </b-form-invalid-feedback>

      <b-form-group label="Select Branch *" class="mt-3">
        <b-form-select
          v-model="selectedBranchId"
          :options="branchOptions"
          :disabled="!selectedClinic"
          :state="$v.selectedBranchId.$dirty ? !$v.selectedBranchId.$error : null"
          @change="$v.selectedBranchId.$touch()"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>
              {{ selectedClinic ? 'Choose a branch...' : 'Select a clinic first' }}
            </b-form-select-option>
          </template>
        </b-form-select>
        <b-form-invalid-feedback :state="!$v.selectedBranchId.$error">
          Please select a branch
        </b-form-invalid-feedback>
      </b-form-group>

      <ExcelFileUploader
        v-model="file"
        label="Upload Excel File *"
        :state="$v.file.$dirty ? !$v.file.$error : null"
        class="mt-3"
        @input="$v.file.$touch()"
      />
      <b-form-invalid-feedback :state="!$v.file.$error">
        Excel file is required
      </b-form-invalid-feedback>

      <b-button type="submit" variant="primary" :disabled="loading" block class="mt-3">
        <i class="fas mr-2" :class="loading ? 'fa-spinner fa-spin' : 'fa-file-import'"></i>
        {{ loading ? 'Importing Patients...' : 'Import Patients' }}
      </b-button>
    </b-form>
  </b-card>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapGetters, mapActions } from 'vuex';
import ExcelFileUploader from './ExcelFileUploader.vue';
import ClinicSearchSelect from './ClinicSearchSelect.vue';

export default {
  name: 'BulkPatientImportForm',
  components: { ExcelFileUploader, ClinicSearchSelect },
  data() {
    return {
      selectedClinic: null,
      selectedBranchId: null,
      file: null
    };
  },
  validations: {
    selectedClinic: { required },
    selectedBranchId: { required },
    file: { required }
  },
  computed: {
    ...mapGetters('moduleSuperadmin', ['isImportPatientsLoading']),
    loading() {
      return this.isImportPatientsLoading;
    },
    branchOptions() {
      if (!this.selectedClinic || !this.selectedClinic.branches) return [];
      return this.selectedClinic.branches.map(branch => ({
        value: branch.branch_id || branch.branchId,
        text: `${branch.branch_code || branch.branchCode} - ${branch.branch_name_th || branch.branchNameTh}`
      }));
    }
  },
  watch: {
    selectedClinic() {
      this.selectedBranchId = null;
    }
  },
  methods: {
    ...mapActions('moduleSuperadmin', ['importPatients']),
    async handleSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('clinicId', String(this.selectedClinic.clinicId || this.selectedClinic.id));
      formData.append('branchId', String(this.selectedBranchId));
      try {
        const result = await this.importPatients(formData);
        this.$emit('success', result.data);
        this.resetForm();
      } catch (error) {
        const message = error.response?.data?.error || 'Failed to import patients';
        this.$bvToast.toast(message, {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 5000
        });
      }
    },
    resetForm() {
      this.selectedClinic = null;
      this.selectedBranchId = null;
      this.file = null;
      this.$v.$reset();
    }
  }
};
</script>
